import { Button, FormControl, Grid, Group, IconButton, Layout, Page } from "@/comn/components";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { useFetch, useForm, useGrid, useModal, usePage, useResource, useStore, useToast, useAuth } from "@/comn/hooks";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    APIS,
    URLS,
    SF_DOC_BOX_MGMT_SRCH,
    SG_DOC_BOX_MGMT_LIST,
} from "@/tra/tancis/ptli/elctdoc/box/services/PtliDocBoxMgmtService";

/*
 * Document Box Management
 * !== 문서함 관리 ==!
 */
export const PTLI0703001Q = () => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0047", "auth_ptli0004", "auth_ptli0015", "auth_ptlianyADM"],
    }); 
    const {t}      = useTranslation();    // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate();       // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast    = useToast();          // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "CGM0176" },  // 내외부 유형 코드
            { area: "comnCd", comnCd: "COM0024" },  // 업무 분류 코드
            { area: "comnCd", comnCd: "COM0064" },  // 사용 여부 코드
            { area: "comnCd", comnCd: "COM0070" },  // 송수신 유형 코드
            { area: "comnCd", comnCd: "COM0077" },  // 송수신 구분 코드
        ], 
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        docBoxMgmtSrch: useForm({
            defaultSchema: SF_DOC_BOX_MGMT_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    const grid = {
        docBoxMgmtList: useGrid({
            defaultSchema: SG_DOC_BOX_MGMT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get a list of document box. !== 문서함 목록을 조회한다. ==!
        getElctDocBoxList: useFetch({
            api: (page = grid.docBoxMgmtList.page) => APIS.getElctDocBoxList(form.docBoxMgmtSrch.getValues(), page, grid.docBoxMgmtList.size),
            enabled: comnUtils.isEmpty(form.docBoxMgmtSrch.errors),
            key: [grid.docBoxMgmtList.page, grid.docBoxMgmtList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.docBoxMgmtSrch.getValues(),
                    page: grid.docBoxMgmtList.page,
                    size: grid.docBoxMgmtList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true
        })
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        getElctDocBoxList: (page: number = 0) => {
            form.docBoxMgmtSrch.handleSubmit(
                () => {
                    grid.docBoxMgmtList.setPage(page);
                    fetch.getElctDocBoxList.fetch(page);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
        // Enter Keyword text !== 검색어 엔터 이벤트 ==!
        onEnterKwdTxt: () => {
            grid.docBoxMgmtList.setPage(0);
            fetch.getElctDocBoxList.fetch(0);
        }
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_docBoxMgmtList: {
            cell: {
                intfNm: (props: any) => {
                    const { rowValues, value } = props;
                    return <Link to={`/ptli/elctdoc/box/ptli0703002s/${rowValues.intfId}`}>{value}</Link>
                },
            },
        },
    };

    const kwdSel = form.docBoxMgmtSrch.watch('kwdSel');

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.docBoxMgmtSrch.schema.bsopClsfCd}></Group.Control>
                            <Group.Control {...form.docBoxMgmtSrch.schema.inexTpCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docBoxMgmtSrch.schema.sndrcvTpCd}></Group.Control>
                            <Group.Control {...form.docBoxMgmtSrch.schema.sndrcvDvnCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Label label={"L_KWD"} />
                            <Group.Col padding={1}>
                                <Layout direction="row" gap={1}>
                                    <FormControl 
                                    {...form.docBoxMgmtSrch.schema.kwdSel}
                                    />
                                    <FormControl 
                                    {...form.docBoxMgmtSrch.schema.kwdTxt} 
                                    readOnly={!kwdSel}
                                    onEnter={handler.onEnterKwdTxt}
                                    />
                                </Layout>
                            </Group.Col>
                            <Group.Control {...form.docBoxMgmtSrch.schema.actvtYn}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.docBoxMgmtSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="search"
                                onClick={() => {
                                    handler.getElctDocBoxList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.docBoxMgmtList.grid}
                            data={fetch.getElctDocBoxList.data?.elctDocBoxList}
                            render={render.grid_docBoxMgmtList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="new"
                        onClick={() => {
                            navigate(URLS.ptli0703003s);
                        }}
                    />
                </Layout.Left>
            </Layout>
        </Page>
    );
};
